import React, { ReactNode, useCallback } from "react";
import { Alert } from "@jugl-web/ui-kit";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useEmailAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/EmailAccountProvider/useEmailAccount";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { ResponseModeOption } from "./components/ResponseModeOption";
import { ReactComponent as ManualIcon } from "../../assets/manual.svg";
import { ReactComponent as AiAgentIcon } from "../../assets/ai-agent.svg";

type ResponseMode = "manual" | "ai";

export const ResponseModeSection: React.FC = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { emailAccount } = useEmailAccount();
  const [updateEmailConfig, { isLoading: isSwitchingReplyMode }] =
    integrationsApi.useUpdateEmailConfigMutation();
  const responseTypeDialogState = useConfirmationDialogState<{
    mode: ResponseMode;
  }>();

  const isAiReplyEnabled = emailAccount?.ai_reply === true;

  const handleConfirmSwitch = useCallback(
    async (mode: ResponseMode) => {
      if (isSwitchingReplyMode) {
        return;
      }

      const nextAiReply = mode === "ai";

      try {
        await updateEmailConfig({ entityId, ai_reply: nextAiReply }).unwrap();
        responseTypeDialogState.close();
        toast(
          nextAiReply
            ? t({
                id: "cx-ai-agents-page.gmail-channel-assigned-to-ai-agent-response",
                defaultMessage: "Gmail channel assigned to AI Agent response",
              })
            : t({
                id: "cx-ai-agents-page.gmail-channel-assigned-to-manual-response",
                defaultMessage: "Gmail channel assigned to manual response",
              })
        );
      } catch {
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          { variant: "error" }
        );
      }
    },
    [
      entityId,
      isSwitchingReplyMode,
      responseTypeDialogState,
      t,
      toast,
      updateEmailConfig,
    ]
  );

  const handleSelectMode = useCallback(
    (mode: ResponseMode) => {
      const isAlreadySelected = (mode === "ai") === isAiReplyEnabled;

      if (isAlreadySelected || isSwitchingReplyMode) {
        return;
      }

      responseTypeDialogState.open({
        onConfirm: () => handleConfirmSwitch(mode),
        metadata: { mode },
      });
    },
    [
      handleConfirmSwitch,
      isAiReplyEnabled,
      isSwitchingReplyMode,
      responseTypeDialogState,
    ]
  );

  return (
    <CRMInfoSection
      title={t({
        id: "client-support-page.who-handle-incoming-emails",
        defaultMessage: "Who handle incoming emails?",
      })}
      subtitle={t({
        id: "client-support-page.choose-who-handles-gmail-responses",
        defaultMessage:
          "Choose who will handle responds to customers via Gmail",
      })}
    >
      <div className="flex flex-col gap-3">
        <ResponseModeOption
          icon={ManualIcon}
          title={t({
            id: "client-support-page.manually-only",
            defaultMessage: "Manually only",
          })}
          description={t({
            id: "cx-ai-agents-page.team-replies-manually",
            defaultMessage: "Team replies manually without AI Assistance",
          })}
          isSelected={!isAiReplyEnabled}
          isDisabled={isSwitchingReplyMode}
          onSelect={() => handleSelectMode("manual")}
        />
        <ResponseModeOption
          icon={AiAgentIcon}
          title={t({
            id: "client-support-page.ai-agent-responds",
            defaultMessage: "AI Agent responds",
          })}
          description={t(
            {
              id: "cx-ai-agents-page.ai-agent-replies-description",
              defaultMessage:
                "Agent send replies automatically{br}and transfer to human agent if needed",
            },
            { br: " " }
          )}
          isSelected={isAiReplyEnabled}
          isDisabled={isSwitchingReplyMode}
          onSelect={() => handleSelectMode("ai")}
        />
      </div>
      <Alert
        isOpen={responseTypeDialogState.isOpen}
        onRequestClose={responseTypeDialogState.close}
        title={t({
          id: "cx-ai-agents-page.response-type",
          defaultMessage: "Response type",
        })}
        content={
          responseTypeDialogState.metadata?.mode === "manual"
            ? t(
                {
                  id: "cx-ai-agents-page.switch-to-manual-response-confirmation",
                  defaultMessage:
                    "Are you sure you want to assign <highlightedText>Manual response</highlightedText> for your Gmail channel? Your Team will reply manually without AI Assistance",
                },
                {
                  highlightedText: (text: ReactNode) => (
                    <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                      {text}
                    </span>
                  ),
                }
              )
            : t(
                {
                  id: "cx-ai-agents-page.assign-ai-agent-response-confirmation",
                  defaultMessage:
                    "Are you sure you want to assign <highlightedText>AI Agent response</highlightedText> for your Gmail channel? Agent will send replies automatically and transfer to human agent if needed",
                },
                {
                  highlightedText: (text: ReactNode) => (
                    <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                      {text}
                    </span>
                  ),
                }
              )
        }
        buttons={[
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            role: "close",
          },
          {
            text: t({ id: "common.confirm", defaultMessage: "Confirm" }),
            onClick: () => responseTypeDialogState.confirm(),
            isDisabled: isSwitchingReplyMode,
          },
        ]}
      />
    </CRMInfoSection>
  );
};
